import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useAssetSettings } from '@nsd/fe';
import history from 'src/utils/history';
import { createDispatchButtonControl } from 'src/constants/functionIdentifiers';
import extractCall, { getCallerPhoneNumber } from 'src/utils/five9/extractCall';
import toDialableNumber from 'src/utils/five9/toDialableNumber';
import { isCallInWrapUp } from 'src/utils/five9/getCallDisplayNumber';
import logger from 'src/utils/five9/five9Logger';
import { emitFive9CallLifecycleEvent } from 'src/utils/five9/five9CallLifecycleEvents';
import {
  upsertCall,
  setActiveCall,
  clearCall,
  setClickToDialEnabled
} from 'src/actions/five9Actions';

/**
 * @typedef {import('src/type-defs/five9Types').Five9Call} Five9Call
 * @typedef {import('src/type-defs/five9Types').Five9AdtConfig} Five9AdtConfig
 * @typedef {import('src/type-defs/five9Types').Five9CrmObject} Five9CrmObject
 * @typedef {import('src/type-defs/five9Types').Five9CrmSearchResult} Five9CrmSearchResult
 * @typedef {import('src/type-defs/five9Types').Five9ScreenPopParams} Five9ScreenPopParams
 */

const PROMISE_RETURNING_HINTS = /^get|^find|^fetch|^search|Count$|Config$/;

export const DISPATCH_CREATE_PATH = '/dispatch/dispatch/dispatch-schedule/create';
// Screens from which a Five9 screen-pop may auto-open dispatch create.
export const DISPATCH_CREATE_AUTO_NAVIGATE_FROM = ['/dispatch/dispatch/dispatch-schedule'];

const screenpopValue = (call, name) => {
  const value = call?.screenpopCore?.[name]?.value;
  return typeof value === 'string' ? value.trim() || null : (value ?? null);
};

/**
 * Query string matching the legacy screen-pop URL contract read by DispatchCreateView.
 *
 * @param {Five9Call} call
 * @returns {string}
 */
export const buildDispatchCreateUrl = (call) => {
  const params = new URLSearchParams();
  const entries = {
    // URL contract (legacy-compatible): `callSid` carries the provider call
    // id — Five9 callId, transfer-stable and equal to the externalCallId Five9
    // sends with transcripts. The per-leg interaction id travels separately.
    callSid: call.callId || call.five9CallId,
    interactionId: call.callSid,
    caller: getCallerPhoneNumber(call),
    company: screenpopValue(call, 'screenpop_core_company_name'),
    did: screenpopValue(call, 'screenpop_core_did'),
    program: screenpopValue(call, 'screenpop_core_program')
  };
  Object.keys(entries).forEach((key) => {
    if (entries[key]) params.set(key, String(entries[key]));
  });
  const query = params.toString();
  return query ? `${DISPATCH_CREATE_PATH}?${query}` : DISPATCH_CREATE_PATH;
};

export const buildCrmSearchObject = (call) => {
  if (!call?.callSid) return null;
  const caller = getCallerPhoneNumber(call);
  const company = screenpopValue(call, 'screenpop_core_company_name');
  if (!caller || !company) return null;
  const did = screenpopValue(call, 'screenpop_core_did');
  const program = screenpopValue(call, 'screenpop_core_program');
  return {
    // callSid keeps the crm object id unique per interaction; the caller's
    // number travels in fields/metadata. The click-to-dial crmObject built by
    // ClickToDialPhone is a separate, dial-scoped object.
    id: call.callSid,
    type: 'DispatchCall',
    label: 'Dispatch Call',
    name: company,
    isWho: true,
    isWhat: false,
    fields: [
      { displayName: 'Company', value: company },
      { displayName: 'Caller', value: caller },
      ...(did ? [{ displayName: 'DID', value: did }] : []),
      ...(program ? [{ displayName: 'Program', value: program }] : [])
    ],
    metadata: { callSid: call.callSid, callerNumber: toDialableNumber(caller) || null }
  };
};

const hideAdtNotification = () => {
  try {
    const appApi = window.Five9?.CrmSdk?.applicationApi?.();
    if (typeof appApi?.hideErrorMessage !== 'function') {
      logger.info('crm.hideErrorMessage.unavailable', {});
      return;
    }
    const result = appApi.hideErrorMessage();
    if (typeof result?.then === 'function') {
      result.then(
        (res) => logger.info('crm.hideErrorMessage', { res: res ?? null }),
        // Known benign: with no message-id param the ADT hides the toast but
        // still rejects { errorCode: 'Generic', errorMessage: 'Invalid param' }.
        (err) => logger.debug('crm.hideErrorMessage.rejected', { error: err?.message || err || null })
      );
    } else {
      logger.info('crm.hideErrorMessage', { res: result ?? null });
    }
  } catch (err) {
    logger.warn('crm.hideErrorMessage.error', { message: err?.message });
  }
};

/**
 * Registers the CRM callbacks used to track calls and trigger dispatch screen-pop flows.
 */
export default function useFive9CrmCallbacks() {
  const dispatch = useDispatch();
  const store = useStore();
  const navigatedCallSidsRef = useRef(new Set());
  // callSid -> reason, for calls waiting on their screen-pop company (or create access).
  const pendingNavigationRef = useRef(new Map());
  const { hasFunctionAccess } = useAssetSettings();
  // Callbacks are registered with the SDK once, so read the latest permission via ref.
  const canCreateDispatch = !!hasFunctionAccess?.(createDispatchButtonControl);
  const canCreateDispatchRef = useRef(false);
  canCreateDispatchRef.current = canCreateDispatch;

  /**
   * Screen-pop: open dispatch create (same as the New Dispatch button) once per call,
   * only from the screens in DISPATCH_CREATE_AUTO_NAVIGATE_FROM so in-progress work isn't lost.
   * Waits for the call's screen-pop company (so the page doesn't fall back to the default
   * workflow) and for create access (asset settings may still be loading).
   */
  const screenPopToDispatchCreateRef = useRef(null);
  screenPopToDispatchCreateRef.current = (callSid) => {
    if (!callSid || navigatedCallSidsRef.current.has(callSid)) return;
    const pending = pendingNavigationRef.current;
    const call = store.getState()?.five9?.calls?.[callSid];
    if (!call) {
      pending.delete(callSid);
      return;
    }
    // Click-to-dial calls originate FROM a dispatch — never open create for them.
    if (call.direction === 'OUTBOUND') {
      pending.delete(callSid);
      navigatedCallSidsRef.current.add(callSid);
      logger.info('screenPop.navigateSkipped', { callSid, reason: 'outbound call' });
      return;
    }
    const defer = (reason) => {
      if (pending.get(callSid) !== reason) {
        pending.set(callSid, reason);
        logger.info('screenPop.navigateDeferred', { callSid, reason });
      }
    };
    if (!screenpopValue(call, 'screenpop_core_company_name')) {
      defer('awaiting screenpop_core_company_name');
      return;
    }
    // Dispatch create matches incomplete sessions by caller, so the URL needs it.
    // On transfers ani/number are agent URIs — only a real phone number counts.
    if (!getCallerPhoneNumber(call)) {
      defer('awaiting caller');
      return;
    }
    if (!canCreateDispatchRef.current) {
      defer('awaiting create dispatch access');
      return;
    }
    pending.delete(callSid);
    // Screen check is final for this call: don't pull the agent away later if they return to the board.
    navigatedCallSidsRef.current.add(callSid);
    const from = (history.location?.pathname || '').replace(/\/+$/, '');
    if (!DISPATCH_CREATE_AUTO_NAVIGATE_FROM.includes(from)) {
      logger.info('screenPop.navigateSkipped', { callSid, from });
      return;
    }
    const to = buildDispatchCreateUrl(call);
    logger.event('screenPop.navigate', { callSid, to });
    history.push(to);
  };

  const retryPendingScreenPops = () => {
    Array.from(pendingNavigationRef.current.keys())
      .forEach((callSid) => screenPopToDispatchCreateRef.current(callSid));
  };

  // Later call events (e.g. interaction.callStarted) may be what delivers the company/caller.
  useEffect(() => {
    let lastCalls = store.getState()?.five9?.calls;
    return store.subscribe(() => {
      const calls = store.getState()?.five9?.calls;
      if (calls === lastCalls) return;
      lastCalls = calls;
      if (pendingNavigationRef.current.size) retryPendingScreenPops();
    });
  }, [store]);

  useEffect(() => {
    if (canCreateDispatch && pendingNavigationRef.current.size) retryPendingScreenPops();
  }, [canCreateDispatch]);

  return useMemo(() => {
    const trackInteraction = (type, args) => {
      const call = extractCall(args);
      if (call) {
        dispatch(upsertCall(call));
        dispatch(setActiveCall(call.callSid));
        if (type === 'search' || type === 'screenPop') {
          logger.event('crm.call.started', call, `via crm.${type}`);
        }
      }
      return call;
    };

    const explicit = {
      // --- Config ---
      /** @returns {Promise<Five9AdtConfig>} */
      getAdtConfig: () => {
        /** @type {Five9AdtConfig} */
        const config = {
          providerName: 'NSD Dispatch Qore',
          myCallsTodayEnabled: false,
          myChatsTodayEnabled: false,
          myEmailsTodayEnabled: false,
          showContactInfo: false
        };
        logger.event('crm.getAdtConfig', { config });
        return Promise.resolve(config);
      },

      // --- Interaction routing ---
      // Returns a best-effort CrmObject built from the call's screenpop CAVs
      // + caller. A non-empty screenPopObject makes the ADT invoke
      // crm.screenPop, which drives navigation (live-verified). Only an
      // empty result arms the direct deferred path — the ADT never fires
      // crm.screenPop for an empty result, so the store-subscription retry
      // is the only trigger when company/caller arrive late.
      // TODO: wire to customer/dispatch lookup service.
      /** @returns {Promise<Five9CrmSearchResult>} */
      search: (...args) => {
        logger.event('crm.search', { args });
        const tracked = trackInteraction('search', args);
        // Read back the merged slice entry — earlier events may have supplied
        // fields (e.g. phoneNumber) this payload lacks.
        const call = store.getState()?.five9?.calls?.[tracked?.callSid] || tracked;
        const crmObject = buildCrmSearchObject(call);
        if (crmObject) {
          logger.event('crm.search.result', { callSid: crmObject.id, crmObject });
          return Promise.resolve({ crmObjects: [crmObject], screenPopObject: crmObject });
        }
        screenPopToDispatchCreateRef.current(tracked?.callSid);
        return Promise.resolve({ crmObjects: [], screenPopObject: null });
      },

      // TODO: wire to dispatch notes action
      /** @returns {Promise<{ success: boolean }>} */
      saveLog: (...args) => {
        logger.event('crm.saveLog', { args });
        const first = Array.isArray(args) ? args[0] : args;
        emitFive9CallLifecycleEvent('crm.saveLog', first);
        const call = extractCall(first);
        if (call) {
          dispatch(upsertCall(call));
          const finalState = String(
            first?.interactionData?.callState || call.state || ''
          ).toUpperCase();
          if (finalState === 'FINISHED') {
            dispatch(clearCall(call.callSid));
            hideAdtNotification();
          }
        }
        // Returning a success payload so Five9's `saveLogResult` is populated
        // instead of `null` in downstream callbacks. TODO: replace `success:true`
        // with the real backend response once wired.
        return Promise.resolve({ success: true });
      },

      // Fired by the ADT when our search result carries a screenPopObject
      // (or on a VCC contact match). Drives dispatch-create navigation.
      // Params shape live-verified 2026-09-30 — see Five9ScreenPopParams:
      // { crmObject, force, interactionType, interactionData,
      //   screenPopObjects: [CrmObject], screenPopType: 'Automatic' }.
      // interactionData carries full call identity, so extractCall is the
      // primary path; crmObject.metadata.callSid (our own echo) is the fallback.
      /** @param {Five9ScreenPopParams[]} args */
      screenPop: (...args) => {
        logger.event('crm.screenPop', { args });
        const call = trackInteraction('screenPop', args);
        const first = Array.isArray(args) ? args[0] : args;
        const metadataCallSid = first?.crmObject?.metadata?.callSid
          || first?.screenPopObjects?.[0]?.metadata?.callSid
          || null;
        screenPopToDispatchCreateRef.current(call?.callSid || metadataCallSid);
        return Promise.resolve();
      },

      bringAppToFront: (...args) => {
        logger.event('crm.bringAppToFront', { args });
        return Promise.resolve();
      },

      // Five9 invokes these when click-to-dial availability changes — on agent
      // login/logout AND around every call (disabled while on a call, re-enabled
      // after wrap-up). The flag gates phone-number links in dispatch views,
      // which also prevents a second dial while the singleton bridge is busy.
      enableClickToDial: (...args) => {
        logger.event('crm.enableClickToDial', { args });
        dispatch(setClickToDialEnabled(true));
        // CONTRACT (live-verified 2026-10-09, manual outbound): click-to-dial
        // stays DISABLED through the whole interaction INCLUDING wrap-up
        // (disable at dial start -> enable only after disposition). This
        // callback therefore means "wrap-up has ended", so clearing here never
        // shortens the linkable-until-dispositioned window. It exists because
        // some endings emit NO terminal event at all (observed:
        // beforeCallFinished(WRAP_UP) -> enableClickToDial and nothing else),
        // which would strand the call in wrap-up forever — callFinished/saveLog
        // cannot be the cleanup signal precisely because they never arrived.
        const five9State = store.getState()?.five9;
        const activeWrapUpCall = five9State?.activeCallSid
          ? five9State.calls?.[five9State.activeCallSid]
          : null;
        if (isCallInWrapUp(activeWrapUpCall)) {
          logger.event(
            'call.wrapUpCleared',
            { callSid: activeWrapUpCall.callSid },
            'wrap-up call cleared on click-to-dial re-enable (no terminal event received)'
          );
          dispatch(clearCall(activeWrapUpCall.callSid));
        }
        return Promise.resolve();
      },

      disableClickToDial: (...args) => {
        logger.event('crm.disableClickToDial', { args });
        // Five9 fires this ~400ms into EVERY dial (click-to-dial is unavailable
        // while the agent is on a call), so the pending click2dial bridge must
        // survive it or no call can ever register (DD2-6464). Stale entries are
        // bounded by the bridge TTLs; real logout/user-switch resets happen in
        // the adapter's auth-transition purge.
        dispatch(setClickToDialEnabled(false));
        return Promise.resolve();
      },

      // --- SDK-private end-of-call hook ---
      // Underscore-prefixed = SDK internal, but Five9 still invokes this on
      // the WRAP_UP → FINISHED transition with the full call + log data
      // (callData.callState, callLogData.duration/handleTime/…, dispositions).
      // Clear the slice when the call reaches FINISHED.
      _beforeCallFinished: (...args) => {
        logger.event('crm.beforeCallFinished', { args });
        try {
          const first = Array.isArray(args) ? args[0] : args;
          const callData = first?.callData;
          if (callData) {
            const call = extractCall(callData);
            if (call) {
              dispatch(upsertCall({ ...call, state: callData.callState || call.state }));
              // WRAP_UP is still "in the interaction"; FINISHED is terminal.
              if (String(callData.callState).toUpperCase() === 'FINISHED') {
                dispatch(clearCall(call.callSid));
              }
            }
          }
        } catch (err) {
          logger.warn('crm.beforeCallFinished.error', { message: err?.message });
        }
        // Return a resolved promise so the SDK doesn't wait on us.
        return Promise.resolve();
      },

    };

    // Proxy wrapper: any property access the SDK performs that isn't in
    // `explicit` returns a synthesized function that logs the call as
    // `crm.<name>` with `unhandled: true` and returns a safe default (a
    // resolved Promise if the name looks like it should return one,
    // otherwise `undefined`). This ensures NOTHING the SDK invokes is
    // silently swallowed.
    return new Proxy(explicit, {
      get(target, prop) {
        if (prop in target) return target[prop];
        // Ignore symbol / internal property probes.
        if (typeof prop !== 'string') return undefined;
        // Don't shadow Object internals used during registration.
        if (prop === 'then' || prop === 'toJSON' || prop === 'constructor') {
          return undefined;
        }
        return (...args) => {
          logger.warn(
            `crm.${prop}`,
            { args, unhandled: true },
            `crmApi invoked an unregistered callback: ${prop}`
          );
          if (PROMISE_RETURNING_HINTS.test(prop)) return Promise.resolve();
          return undefined;
        };
      }
    });
  }, [dispatch, store]);
}
